import { shallowEquals } from '../../util/equals';
import findOrInsert from '../../util/findOrInsert';
import { declaratieVerantwoordingListUsed, RequestOutput } from '../../requests';
import { DbUser, VerantwoordingLegacy } from '../../types';
import isAuthorized from '../../util/isAuthorized';
import findAndDelete from '../../util/findAndDelete';
import { totalCap, monthCap } from '../../util/omvangCap';
import calculateStatementAmount from '../../util/calculateStatementAmount';
import { FILTER_DATE_MAX } from '../../configuration';

export type UsedData = RequestOutput<typeof declaratieVerantwoordingListUsed> | undefined;

const HAS_TARIEF_MESSAGE = 'Voor deze contract regels zijn er nog geen tarieven bekent. Deze kan niet automatisch ingedient worden. Contacteer zorgverkeer support als dit opgepakt moet worden.';
const OUT_OF_ASSIGNMENT_MESSAGE = 'Deze verantwoording valt buiten de toewijzingsperiode en kan niet ingediend worden.';
const CREDIT_WITHOUT_REDECLARATION_MESSAGE = 'Voor deze periode en toewijzing wordt een credit ingediend. Selecteer deze verantwoording ook als hij opnieuw gedeclareerd moet worden.';
const ALREADY_DECLARED_MESSAGE = 'Je kan deze verantwoording niet declareren, omdat er in deze periode al eens is gedeclareerd. Crediteer de bestaande toewijzing.';
const TR322_MESSAGE = 'Deze verantwoording overschrijdt het totaal gedeclareerd volume van de toewijzing en kan niet ingediend worden';
const TR321_MESSAGE = 'Deze verantwoording overschrijdt het maximum gedeclareerde volume voor periode en kan niet ingediend worden';
const TR369_MESSAGE = 'Deze verantwoording overschrijdt het totaal gedeclareerde bedrag van het toegewezen budget en kan niet ingediend worden';
const BLOCKING_SUMMARY = 'Niet alle geselecteerde verantwoordingen kunnen ingediend worden. Zie de tip bij de verantwoording.';

/**
 *  Adds `amountOf(row)` for each row in order, on top of `baseline`, and flags
 *  the rows where the running total goes over `cap`. So only the verantwoordingen
 *  that tip the balance get a message.
 */
function flagOverflowingRows(
	rows: VerantwoordingLegacy[],
	baseline: number,
	cap: number,
	amountOf: (row: VerantwoordingLegacy) => number,
	message: string,
	flag: (id: number, message: string) => void,
) {
	let used = baseline;
	for (const row of rows) {
		used += amountOf(row);
		if (used > cap) flag(row.id, message);
	}
}

/**
 *  TR322/TR321/TR369 (github #335): sum of ingediend+goedgekeurd volume/bedrag
 *  for a toewijzing must not exceed its toegewezen volume/budget, either
 *  totaal (TR322/TR369) or for a single declaratieperiode (TR321). Grouped by
 *  `toewijzing_id` (and, for TR321, further by `periode_id`) across the
 *  currently *selected* rows, added on top of the server-computed
 *  `volume_gebruikt`/`volume_gebruikt_periode`/`bedrag_gebruikt` baseline
 *  (declaratieregels that already exist outside this unprocessed batch).
 */
function omvangFlags(data: VerantwoordingLegacy[], selected: number[], flag: (id: number, message: string) => void) {
	const toewijzingIds = [...new Set(data.map(row => row.toewijzing_id))];
	for (const toewijzing_id of toewijzingIds) {
		const rows = data.filter(row => row.toewijzing_id === toewijzing_id);
		const selectedRows = rows.filter(row => selected.includes(row.id));
		if (selectedRows.length === 0) continue;
		const representative = rows[0];
		const toewijzingBegindatum = new Date(representative.toewijzing_begindatum);
		// null means the toewijzing never expires - treat it as far-future, same convention as
		// FormAllocationStatement.tsx's 305/307 clamp logic.
		const toewijzingEinddatum = new Date(representative.toewijzing_einddatum ?? FILTER_DATE_MAX);

		// TR322/TR321 need both volume and frequentie - a toewijzing allocated via Budget
		// instead of Omvang (CDT_Omvang: volume+eenheid+frequentie are all-or-nothing together)
		// has neither set, so there's no volume cap to check at all.
		if (representative.toewijzing_volume !== null && representative.toewijzing_frequentie !== null) {
			const toewijzingVolume = representative.toewijzing_volume;
			const toewijzingFrequentie = representative.toewijzing_frequentie;

			// TR322: total volume over the whole toewijzing
			const totalVolumeCap = totalCap(toewijzingVolume, toewijzingFrequentie, toewijzingBegindatum, toewijzingEinddatum);
			flagOverflowingRows(selectedRows, representative.volume_gebruikt, totalVolumeCap, row => row.volume, TR322_MESSAGE, flag);

			// TR321: volume vs monthly cap, scoped per declaratieperiode (skip when the frequentie has no monthly cadence)
			const periodeIds = [...new Set(selectedRows.map(row => row.periode_id))];
			for (const periode_id of periodeIds) {
				const periodeRows = selectedRows.filter(row => row.periode_id === periode_id);
				const monthlyCap = monthCap(toewijzingVolume, toewijzingFrequentie, new Date(periodeRows[0].begindatum));
				if (monthlyCap === null) continue;
				flagOverflowingRows(periodeRows, periodeRows[0].volume_gebruikt_periode, monthlyCap, row => row.volume, TR321_MESSAGE, flag);
			}
		}

		// TR369: total bedrag vs budget (skip when no budget was given)
		if (representative.toewijzing_budget !== null) {
			flagOverflowingRows(selectedRows, representative.bedrag_gebruikt, representative.toewijzing_budget, calculateStatementAmount, TR369_MESSAGE, flag);
		}
	}
}

type PeriodeToewijzing = [VerantwoordingLegacy['periode_id'], VerantwoordingLegacy['toewijzing_id']];

/**
 *  The periode/toewijzing combinations that already have a declaratieregel which
 *  was not credited (github #440): a new verantwoording there can't be declared
 *  until the existing one is credited.
 */
function getDeclaredEntries(usedData: UsedData): PeriodeToewijzing[] {
	const entries: PeriodeToewijzing[] = [];
	for (const row of usedData ?? []) {
		if (row.is_credit) {
			findAndDelete(entries, [row.periode_id, row.toewijzing_id], shallowEquals);
		} else {
			findOrInsert(entries, [row.periode_id, row.toewijzing_id], shallowEquals);
		}
	}
	return entries;
}

/**
 *  Why a verantwoording can't be declared at all, independent of what is selected.
 *  Such a row can't be selected, is left out of "select all", and is never sent.
 *
 *  A credit is always declarable, because it is always sent with the next declaratie
 *  (`InvoiceMain.handleSend`). It repeats the tarief of the declaratieregel it credits
 *  (so the current tarief doesn't matter), the server already exempts it from the
 *  toewijzingsperiode check (#567), and crediting is exactly what frees an
 *  already-declared periode again.
 */
function undeclarableReasons(row: VerantwoordingLegacy, declaredEntries: PeriodeToewijzing[]): string[] {
	if (row.previeus_declaratieregel_id !== null) return [];
	const reasons: string[] = [];
	if (!row.has_tarief) reasons.push(HAS_TARIEF_MESSAGE);
	if (row.is_out_of_assignment === 1) reasons.push(OUT_OF_ASSIGNMENT_MESSAGE);
	if (declaredEntries.find(e => shallowEquals(e, [row.periode_id, row.toewijzing_id]))) {
		reasons.push(ALREADY_DECLARED_MESSAGE);
	}
	return reasons;
}

/** Returns whether a verantwoording can be declared, see `undeclarableReasons`. */
export function makeIsDeclarable(usedData: UsedData): (row: VerantwoordingLegacy) => boolean {
	const declaredEntries = getDeclaredEntries(usedData);
	return row => undeclarableReasons(row, declaredEntries).length === 0;
}

/**
 *  Every row-specific reason a verantwoording can't be declared, keyed by its id,
 *  so it can be shown as a tooltip on that row.
 *
 *  `selected` is the part of the selection that will actually be sent: rows that
 *  can't be declared are dropped from it (e.g. rows selected before `usedData` had
 *  loaded). `blocking` is true when a selected row stops the declaratie from being
 *  generated. A row that is not selected never blocks: its tip is informational.
 */
export function getRowWarnings({ data, selected, usedData }: {
	data: VerantwoordingLegacy[],
	selected: number[],
	usedData: UsedData,
}): { tips: Map<number, string[]>, blocking: boolean, selected: number[] } {
	const tips = new Map<number, string[]>();
	let blocking = false;
	const flag = (id: number, message: string) => {
		tips.set(id, [...(tips.get(id) ?? []), message]);
	};

	const declaredEntries = getDeclaredEntries(usedData);
	const declarableIds = new Set<number>();
	for (const row of data) {
		const reasons = undeclarableReasons(row, declaredEntries);
		for (const reason of reasons) flag(row.id, reason);
		if (reasons.length === 0) declarableIds.add(row.id);
	}
	const sendable = selected.filter(id => declarableIds.has(id));

	// A selected credit is sent without the verantwoording that re-declares the same
	// periode and toewijzing: warn on that row, the user may want to select it too.
	const creditedEntries: PeriodeToewijzing[] = [];
	for (const row of data) {
		if (row.previeus_declaratieregel_id !== null && sendable.includes(row.id)) {
			findOrInsert(creditedEntries, [row.periode_id, row.toewijzing_id], shallowEquals);
		}
	}
	for (const row of data) {
		if (declarableIds.has(row.id) && !sendable.includes(row.id) && creditedEntries.find(e => shallowEquals(e, [row.periode_id, row.toewijzing_id]))) {
			flag(row.id, CREDIT_WITHOUT_REDECLARATION_MESSAGE);
		}
	}

	omvangFlags(data, sendable, (id, message) => {
		flag(id, message);
		blocking = true;
	});
	return { tips, blocking, selected: sendable };
}

export function checkDisabledReasons({
	data,
	selected,
	loading,
	dbUser,
	usedData,
}: Pick<DeclaratieButtons, 'data' | 'selected' | 'dbUser' | 'loading'> & {
	usedData: UsedData
}) {
	const warnings = new Set<string>();
	if (!isAuthorized(dbUser.autorisatie, 'functie.uitvoeren')) {
		warnings.add('Jou account heeft geen rechten voor deze functie');
	}
	if (loading) {
		warnings.add('');
	}
	const rowWarnings = getRowWarnings({ data, selected, usedData });
	if (rowWarnings.selected.length === 0) {
		warnings.add('Selecteer tenminste 1 verantwoording');
	}
	if (rowWarnings.blocking) {
		warnings.add(BLOCKING_SUMMARY);
	}
	return [...warnings];
}

interface DeclaratieButtons {
	dbUser: DbUser,
	selected: number[],
	data: VerantwoordingLegacy[],
	usedData: UsedData,
	loading: boolean | undefined,
	handleSend: () => void
}
export function DeclaratieButtons({ selected, data, dbUser, usedData, loading, handleSend }: DeclaratieButtons) {
	const sendableCount = getRowWarnings({ data, selected, usedData }).selected.length;
	let label: string;
	if (data.length === 0) {
		label = '';
	} else if (sendableCount <= 1) {
		label = 'Declaratie aanmaken voor deze verantwoording';
	} else {
		label = `Declaraties aanmaken voor ${sendableCount} verantwoordingen`;
	}
	const disabledReasons: string[] = checkDisabledReasons({
		data,
		dbUser,
		loading: loading || !usedData,
		selected,
		usedData,
	});
	if (!label) return null;
	return (
		<section className='table-footer-top'>
			<form>
				<button
					type='button'
					disabled={disabledReasons.length > 0}
					className={'primary no-margin'}
					onClick={event => {
						event.preventDefault();
						if (disabledReasons.length === 0) {
							handleSend();
						}
					}}
				>
					{label}
				</button>
				<ul>
					{!loading && disabledReasons.map(e => (
						<li className='error-color' key={e}>
							{e}
						</li>
					))}
				</ul>
			</form>
		</section>
	);
}
